// ═════════════════════════════════════���═════════════════════════
// TaskRow — Individual task row for the Director Task Manager
// Path: src/components/tasks/TaskRow.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import {
  CheckCircle2,
  Circle,
  Zap,
  Pencil,
  Trash2,
  User,
  Notebook,
  RotateCw,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { TASK_PRIORITIES, TASK_CATEGORIES } from "../../constants/taskTypes";
import { useApp } from "../../context/AppContext";

// ── Helpers ──────────────────────────────────────────────────

const getPriority = (id) => TASK_PRIORITIES.find((p) => p.id === id) || TASK_PRIORITIES[1];
const getCategory = (id) => TASK_CATEGORIES.find((c) => c.id === id) || TASK_CATEGORIES[7];

const formatDueDate = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, color: "#ef4444", isOverdue: true };
  if (diffDays === 0) return { label: "Today", color: "#f59e0b", isOverdue: false };
  if (diffDays === 1) return { label: "Tomorrow", color: "#3b82f6", isOverdue: false };
  if (diffDays <= 7) return { label: `${diffDays}d`, color: "#3b82f6", isOverdue: false };
  if (diffDays <= 30) return { label: `${Math.floor(diffDays / 7)}w`, color: "#6b7280", isOverdue: false };
  return { label: `${Math.floor(diffDays / 30)}mo`, color: "#6b7280", isOverdue: false };
};

// ── Component ────���───────────────────────────────────────────

export const TaskRow = ({
  task,
  palette,
  members,
  onComplete,
  onReopen,
  onEdit,
  onDelete,
  onNavigateToDonor,
}) => {
  const { setPrintContactReportId } = useApp();
  const [hover, setHover] = useState(false);
  const isCompleted = task.status === "completed";
  const priority = getPriority(task.priority);
  const category = getCategory(task.category);
  const dueInfo = formatDueDate(task.dueDate);

  // Find linked donor name
  const linkedMember = task.memberId
    ? members.find((m) => m.id === task.memberId)
    : null;
  const donorName = linkedMember
    ? `${linkedMember.firstName || ""} ${linkedMember.lastName || ""}`.trim()
    : null;

  const handleToggle = () => {
    if (isCompleted) {
      onReopen?.(task.id);
    } else {
      onComplete?.(task.id);
    }
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        borderRadius: 10,
        background: hover ? `${palette.accent1}06` : "transparent",
        transition: "background 0.15s",
        opacity: isCompleted ? 0.5 : 1,
        fontFamily: FONT_FAMILY,
      }}
    >
      {/* Priority dot */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: priority.color,
          flexShrink: 0,
        }}
        title={`${priority.name} priority`}
      />

      {/* Checkbox */}
      <button
        onClick={handleToggle}
        title={isCompleted ? "Reopen task" : "Mark complete"}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {isCompleted ? (
          <CheckCircle2 size={ICON_SIZES.md} style={{ color: "#10b981" }} />
        ) : (
          <Circle size={ICON_SIZES.md} style={{ color: palette.text3 }} />
        )}
      </button>

      {/* Title + donor chip */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 500,
              color: isCompleted ? palette.text3 : palette.text1,
              textDecoration: isCompleted ? "line-through" : "none",
              cursor: task.memberId && onNavigateToDonor ? "pointer" : "default",
            }}
            onClick={() => {
              if (task.memberId && onNavigateToDonor) {
                onNavigateToDonor(linkedMember);
              }
            }}
          >
            {task.title}
          </span>
          {task.recurrenceId && (
            <RotateCw
              size={ICON_SIZES.xs}
              style={{ color: "#0ea5e9", flexShrink: 0 }}
              title="Part of a repeating series"
            />
          )}
          {task.isAuto && task.sourceReportId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPrintContactReportId(task.sourceReportId);
              }}
              title="Open source contact report"
              style={{
                display: "flex",
                alignItems: "center",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Notebook size={ICON_SIZES.xs} style={{ color: "#8b5cf6" }} />
            </button>
          )}
          {task.isAuto && !task.sourceReportId && (
            <Zap
              size={ICON_SIZES.xs}
              style={{ color: "#f59e0b", flexShrink: 0 }}
              title={
                task.sourceMove
                  ? "Auto-generated from portfolio move"
                  : "Auto-generated"
              }
            />
          )}
        </div>
        {donorName && (
          <button
            onClick={() => onNavigateToDonor?.(linkedMember)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: `${palette.accent1}12`,
              border: "none",
              borderRadius: 4,
              padding: "1px 6px",
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              color: palette.accent1,
              cursor: "pointer",
              marginTop: 2,
            }}
          >
            <User size={ICON_SIZES.xs} />
            {donorName}
          </button>
        )}
      </div>

      {/* Category badge */}
      <span
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 6,
          background: `${category.color}15`,
          color: category.color,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {category.name}
      </span>

      {/* Due date */}
      {dueInfo && (
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: dueInfo.color,
            whiteSpace: "nowrap",
            minWidth: 60,
            textAlign: "right",
            flexShrink: 0,
          }}
        >
          {dueInfo.label}
        </span>
      )}
      {!dueInfo && (
        <span style={{ minWidth: 60, flexShrink: 0 }} />
      )}

      {/* Actions (visible on hover) */}
      <div
        style={{
          display: "flex",
          gap: 4,
          opacity: hover ? 1 : 0,
          transition: "opacity 0.15s",
          flexShrink: 0,
        }}
      >
        {!isCompleted && onEdit && (
          <button
            onClick={() => onEdit(task)}
            title="Edit task"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Pencil size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(task.id)}
            title="Delete task"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={ICON_SIZES.sm} style={{ color: "#ef4444" }} />
          </button>
        )}
      </div>
    </div>
  );
};
